import { useEffect, useRef, useState, type JSX } from 'react';
import { Link, Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { BadgeCheck, FileText, Gift, KeyRound, LogOut, UserRound } from 'lucide-react';
import { LogoMark } from '@caratuva/ui';
import { clearToken, getToken, isAuthenticated, isTokenExpired } from '../lib/auth';
import { PasskeySetupDialog } from '../components/passkey-setup';

/*
 * Authenticated shell for GetPaid (B2C).
 *
 * `beforeLoad` enforces authentication only — it runs before any child loader
 * fires, so unauthenticated (or expired) users never trigger an API fetch on
 * their way to /login. GetPaid pins the org to a single build-time mode
 * (lib/auth.ensureConfiguredMode), so there is deliberately NO mode switcher,
 * notifications bell, or any nav beyond the three product surfaces.
 */

export const Route = createFileRoute('/_app')({
  beforeLoad: () => {
    // An expired-but-present token is a "your session died" event: clear it and
    // send the user to /login with reason=expired so the page can explain why.
    if (getToken() && isTokenExpired()) {
      clearToken();
      throw redirect({
        to: '/login',
        search: { reason: 'expired' },
      });
    }
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' });
    }
  },
  component: AppShell,
});

const NAV_LINK_CLASS =
  'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900';

function UserMenu({ onAddPasskey }: { onAddPasskey: () => void }): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function onSignOut(): void {
    clearToken();
    void navigate({ to: '/login' });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.formatMessage({
          id: 'shell.userMenu.open',
          defaultMessage: 'Account menu',
        })}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-navy-200 bg-white text-navy-700 hover:bg-navy-50"
      >
        <UserRound aria-hidden className="h-4 w-4" strokeWidth={1.5} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-md border border-navy-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAddPasskey();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-navy-800 hover:bg-navy-50"
          >
            <KeyRound aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({
              id: 'shell.userMenu.addPasskey',
              defaultMessage: 'Add a passkey',
            })}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-navy-800 hover:bg-navy-50"
          >
            <LogOut aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'shell.userMenu.signOut', defaultMessage: 'Sign out' })}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AppShell(): JSX.Element {
  const intl = useIntl();
  const [passkeyOpen, setPasskeyOpen] = useState(false);

  // Kick an idle tab out once its JWT expires, even if it never issues an API
  // call that would otherwise trigger the 401 interceptor in lib/api.ts.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (isTokenExpired()) {
        clearToken();
        window.location.assign('/login?reason=expired');
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="grid min-h-screen grid-cols-[16rem_1fr]">
      <aside className="border-r border-navy-200 bg-white">
        <div className="px-6 py-5">
          <LogoMark size={24} />
        </div>
        <nav className="px-3">
          <Link to="/onboarding" className={NAV_LINK_CLASS} activeProps={{ className: 'active' }}>
            <BadgeCheck aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'common.nav.onboarding', defaultMessage: 'Onboarding' })}
          </Link>
          <Link
            to="/invoices"
            className={NAV_LINK_CLASS}
            activeProps={{ className: 'active' }}
            activeOptions={{ exact: false, includeSearch: false }}
          >
            <FileText aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'common.nav.invoices', defaultMessage: 'Invoices' })}
          </Link>
          <Link to="/affiliate" className={NAV_LINK_CLASS} activeProps={{ className: 'active' }}>
            <Gift aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'common.nav.affiliate', defaultMessage: 'Affiliate' })}
          </Link>
        </nav>
      </aside>

      <div className="flex min-h-screen flex-col">
        <header className="flex items-center justify-end gap-2 border-b border-navy-200 bg-white px-6 py-3">
          <UserMenu onAddPasskey={() => setPasskeyOpen(true)} />
        </header>
        <main className="flex-1 px-6 py-6">
          <Outlet />
        </main>
      </div>

      <PasskeySetupDialog open={passkeyOpen} onOpenChange={setPasskeyOpen} />
    </div>
  );
}
